import React, { useEffect, useState } from "react";

//MUI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

// Components
import WinProbabilityFactor from "../win-probability-factor";
import TextBox from "../../TextBox";

// Hooks
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux/hooks/redux-hooks";

// Actions
import {
  setWinProbability,
  setGoFactorPercentage,
  setGetFactorPercentage,
  setManualGoFactorPercentage,
  setManualGetFactorPercentage,
  setManualGoGetFactorPercentage,
  setManualGoGetFactorRemarks,
} from "../../../redux/actions/ProjectActions";
import {
  setGoFactors,
  setGetFactors,
} from "../../../redux/actions/WinProbabilityFactorActions";

// Models
import { AddEdit } from "../../../SectionWiseClassification";
import { factorsObj } from "../../../models/WinProbabilityFactorModel";

type Props = {
  type: AddEdit;
};

const GoGetFactors = ({ type }: Props) => {
  const [goFactorsNACount, setGoFactorsNACount] = useState(0);
  const [getFactorsNACount, setGetFactorsNACount] = useState(0);

  const winFactorQuestions = useAppSelector((state) => state.wpf.questions);
  const goFactors = useAppSelector((state) => state.wpf.goFactors);
  const getFactors = useAppSelector((state) => state.wpf.getFactors);

  const winProbability = useAppSelector(
    (state) => state.project.project_Object.winProbabilityPercentage
  );

  const goFactorPercentage = useAppSelector(
    (state) => state.project.project_Object.goFactorPercentage
  )!;
  const getFactorPercentage = useAppSelector(
    (state) => state.project.project_Object.getFactorPercentage
  )!;

  const manualGoGetFactor = useAppSelector(
    (state) => state.project.project_Object.manualGoGetFactorPercentage
  )!;

  const manualGoFactorPercentage = useAppSelector(
    (state) => state.project.project_Object.manualGoFactorPercentage
  )!;

  const manualGetFactorPercentage = useAppSelector(
    (state) => state.project.project_Object.manualGetFactorPercentage
  )!;

  const manualGoGetFactorRemarks = useAppSelector(
    (state) => state.project.project_Object.manualGoGetFactorRemarks
  );

  const projectQuestions = useAppSelector(
    (state) => state.wpf.projectQuestions
  );

  const dispatch = useAppDispatch();

  //To set the win proability factors
  useEffect(() => {
    if (type === "edit") {
      // To set go factors
      if (
        projectQuestions.goFactorSelectedOptions &&
        projectQuestions.goFactorSelectedOptions.length !== 0
      ) {
        let goFactors: factorsObj =
          projectQuestions.goFactorSelectedOptions.reduce((acc, val) => {
            return {
              ...acc,
              [val.question.questionId]: val.option?.optionId || 0,
            };
          }, {});
        dispatch(setGoFactors(goFactors));
      }

      // To set get factors
      if (
        projectQuestions.getFactorSelectedOptions &&
        projectQuestions.getFactorSelectedOptions.length !== 0
      ) {
        let getFactors: factorsObj =
          projectQuestions.getFactorSelectedOptions.reduce((acc, val) => {
            return {
              ...acc,
              [val.question.questionId]: val.option?.optionId || 0,
            };
          }, {});
        dispatch(setGetFactors(getFactors));
      }
    }
  }, [projectQuestions, dispatch, type]);

  // To get the go factor value
  useEffect(() => {
    let result = 0;
    let count = 0;
    Object.keys(goFactors).forEach((questionId) => {
      const question = winFactorQuestions?.goFactorQuestions.find(
        (rec) => rec.question.questionId === +questionId
      );
      if (question) {
        const option = question.options.find(
          (rec) => rec.optionId === goFactors[questionId]
        );
        if (option) {
          result +=
            question.question.questionWeightage * option.optionWeightage;

          if (option.optionDetails.toUpperCase() === "NA") {
            count++;
          }
        }
      }
    });
    setGoFactorsNACount(count);
    dispatch(setGoFactorPercentage(+result.toFixed(2)));
  }, [goFactors, winFactorQuestions, dispatch]);

  // To get the get factor value
  useEffect(() => {
    let result = 0;
    let count = 0;
    Object.keys(getFactors).forEach((questionId) => {
      const question = winFactorQuestions?.getFactorQuestions.find(
        (rec) => rec.question.questionId === +questionId
      );
      if (question) {
        const option = question.options.find(
          (rec) => rec.optionId === getFactors[questionId]
        );
        if (option) {
          result +=
            question.question.questionWeightage * option.optionWeightage;
          if (option.optionDetails.toUpperCase() === "NA") {
            count++;
          }
        }
      }
    });
    setGetFactorsNACount(count);
    dispatch(setGetFactorPercentage(+result.toFixed(2)));
  }, [getFactors, winFactorQuestions, dispatch]);

  // To set the win probability value
  useEffect(() => {
    const winProbabilityVal = (goFactorPercentage * getFactorPercentage) / 100;
    dispatch(setWinProbability(+winProbabilityVal.toFixed(2)));
  }, [goFactorPercentage, getFactorPercentage, dispatch]);

  // To set the manual go get factor value
  useEffect(() => {
    const manualGoGetFactor =
      (manualGoFactorPercentage * manualGetFactorPercentage) / 100;
    dispatch(setManualGoGetFactorPercentage(+manualGoGetFactor.toFixed(2)));
  }, [manualGoFactorPercentage, manualGetFactorPercentage, dispatch]);

  // To re set the manual go get factor remarks
  useEffect(() => {
    if (!manualGoFactorPercentage && !manualGetFactorPercentage) {
      dispatch(setManualGoGetFactorRemarks(""));
    }
  }, [manualGoFactorPercentage, manualGetFactorPercentage, dispatch]);

  // To handle win factor question option changes
  const handleFactorChange = (
    factor: "GetFactor" | "GoFactor",
    optionId: number,
    questionId: number
  ) => {
    if (factor === "GoFactor") {
      dispatch(
        setGoFactors({
          ...goFactors,
          [questionId]: optionId,
        })
      );
    } else {
      dispatch(
        setGetFactors({
          ...getFactors,
          [questionId]: optionId,
        })
      );
    }
  };

  const getManualFactorValue = (numVal: number) => {
    if (numVal > 100) {
      numVal = 100;
    } else if (numVal < 0) {
      numVal = 0;
    }
    return numVal;
  };

  const onChangeManualGoFactor = (val: string) => {
    dispatch(
      setManualGoFactorPercentage(
        val ? getManualFactorValue(parseFloat(val)) : 0
      )
    );
  };

  const onChangeManualGetFactor = (val: string) => {
    dispatch(
      setManualGetFactorPercentage(
        val ? getManualFactorValue(parseFloat(val)) : 0
      )
    );
  };

  const onChangeManualGoGetFactorRemarks = (val: string) => {
    dispatch(setManualGoGetFactorRemarks(val));
  };

  return (
    <Box
      sx={{
        padding: "1em",
        border: "1px solid rgb(0 0 0 / 18%)",
        borderRadius: "0.375rem",
      }}
    >
      <Typography variant="button" style={{ fontWeight: "bold" }}>
        Go Get Factor: {winProbability}%
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ marginTop: "1em" }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              Go Factors:
            </Typography>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              NA Count: {goFactorsNACount}
            </Typography>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              Value: {goFactorPercentage}
            </Typography>
          </Stack>
          <Divider sx={{ marginBottom: "1em", backgroundColor: "black" }} />
          {winFactorQuestions?.goFactorQuestions.map((rec, index) => (
            <WinProbabilityFactor
              key={rec.question.questionId}
              index={index + 1}
              question={rec.question}
              options={rec.options}
              value={goFactors[rec.question.questionId] || null}
              onChange={handleFactorChange}
            />
          ))}
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ marginTop: "1em" }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              Get Factors:
            </Typography>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              NA Count: {getFactorsNACount}
            </Typography>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              Value: {getFactorPercentage}
            </Typography>
          </Stack>
          <Divider sx={{ marginBottom: "1em", backgroundColor: "black" }} />
          {winFactorQuestions?.getFactorQuestions.map((rec, index) => (
            <WinProbabilityFactor
              key={rec.question.questionId}
              index={index + 1}
              question={rec.question}
              options={rec.options}
              value={getFactors[rec.question.questionId] || null}
              onChange={handleFactorChange}
            />
          ))}
        </Grid>
      </Grid>
      <Typography variant="button" style={{ fontWeight: "bold" }}>
        Manual Go Get Factor: {manualGoGetFactor}%
      </Typography>
      <Divider sx={{ marginBottom: "1em", backgroundColor: "black" }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextBox
            label="Manual Go Factor (%)"
            placeholder="Enter Manual Go Factor"
            size="small"
            fullWidth
            inputProps={{
              max: 100,
              min: 0,
            }}
            value={manualGoFactorPercentage || ""}
            changeHandler={onChangeManualGoFactor}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextBox
            label="Manual Get Factor (%)"
            placeholder="Enter Manual Get Factor"
            size="small"
            fullWidth
            inputProps={{
              max: 100,
              min: 0,
            }}
            value={manualGetFactorPercentage || ""}
            changeHandler={onChangeManualGetFactor}
          />
        </Grid>
        {manualGoFactorPercentage || manualGetFactorPercentage ? (
          <Grid item xs={12} md={4}>
            <TextBox
              label="Manual Go Get Factor Remarks"
              placeholder="Enter Manual Go Get Factor Remarks"
              multiline
              maxRows={2}
              size="small"
              fullWidth
              required
              value={manualGoGetFactorRemarks || ""}
              changeHandler={onChangeManualGoGetFactorRemarks}
            />
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default GoGetFactors;
